// Hooks de datos de Segmentos. Las URLs viven en `api/services/loan-offerings/`.
// SIN MOCK: si la lectura falla, la pantalla lo dice.
import { useMemo } from 'react';
import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import type {
    CreateSegmentRequest,
    SegmentResponse,
    SegmentStatusWire,
    TestCustomerSegmentsResponse,
    UpdateSegmentRequest,
} from '@/api/services/loan-offerings/segments.contract';
import {
    changeSegmentStatus,
    createSegment,
    getSegmentUsage,
    listSegmentLog,
    listSegments,
    testCustomerSegments,
    updateSegment,
} from '@/api/services/loan-offerings/loan-offerings.client';
import { COHORTS_KEY } from '../../cohorts/api/keys';
import type { DefinitionLogState } from '../components/DefinitionLogDialog';
import { isStaleWriteError } from '../editorSync';
import type { Segment, SegmentUsage } from '../types';
import { toSegment, toSegmentLogEntry, toUsage } from './mappers';

/** Raíz de las query keys de la pantalla; la UI la usa para reintentar toda la lectura. */
export const SEGMENTS_KEY = 'segments';

const EMPTY_SEGMENTS: Segment[] = [];

export interface SegmentsResult {
    segments: Segment[];
    isLoading: boolean;
    error: Error | null;
}

/** `ACTIVE` para los selectores de otras pantallas; `ALL` para la pantalla, que filtra en el front. */
export type SegmentsScope = 'ACTIVE' | 'ALL';

/** `enabled` cablea el gate RBAC: sin `loan.segment.read` el request ni se dispara. */
export function useSegments(enabled = true, scope: SegmentsScope = 'ACTIVE'): SegmentsResult {
    const query = useQuery({
        queryKey: [SEGMENTS_KEY, 'list', scope],
        queryFn: () => listSegments(scope === 'ALL' ? undefined : 'ACTIVE'),
        enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });

    const segments = useMemo(
        () => (query.data ? query.data.map(toSegment) : EMPTY_SEGMENTS),
        [query.data],
    );

    // Con la query deshabilitada `isLoading` de react-query sigue en `true`: sin esto, el rol sin
    // permiso vería un cargando eterno en vez del aviso.
    return {
        segments,
        isLoading: enabled && query.isLoading,
        error: enabled ? query.error : null,
    };
}

export interface SegmentUsageResult {
    usage: SegmentUsage | null;
    isLoading: boolean;
    error: Error | null;
}

/** Políticas y promociones que apuntan al segmento → GET /segments/{id}/usage. */
export function useSegmentUsage(segmentId: string | null, enabled: boolean): SegmentUsageResult {
    const active = enabled && segmentId !== null;
    const query = useQuery({
        queryKey: [SEGMENTS_KEY, 'usage', segmentId ?? ''],
        queryFn: () => getSegmentUsage(segmentId ?? ''),
        enabled: active,
        staleTime: 30_000,
    });
    const usage = useMemo(() => (query.data ? toUsage(query.data) : null), [query.data]);
    return {
        usage,
        isLoading: active && query.isLoading,
        error: active ? query.error : null,
    };
}

/** Un cambio en un segmento mueve también el «Usado en» de sus cohorts. */
function useInvalidateSegments() {
    const queryClient = useQueryClient();
    return () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: [SEGMENTS_KEY] }),
            queryClient.invalidateQueries({ queryKey: [COHORTS_KEY] }),
        ]);
}

/** Alta de un segmento → POST /segments. */
export function useCreateSegment() {
    const invalidate = useInvalidateSegments();
    return useMutation<SegmentResponse, Error, CreateSegmentRequest>({
        mutationFn: (body) => createSegment(body),
        onSuccess: invalidate,
    });
}

export interface UpdateSegmentInput {
    segmentId: string;
    patch: UpdateSegmentRequest;
}

/** Edición parcial → PUT /segments/{segmentId}. Un 404 o 409 relee: lo abierto quedó viejo. */
export function useUpdateSegment() {
    const invalidate = useInvalidateSegments();
    return useMutation<SegmentResponse, Error, UpdateSegmentInput>({
        mutationFn: ({ segmentId, patch }) => updateSegment(segmentId, patch),
        onSuccess: invalidate,
        onError: (error) => {
            if (isStaleWriteError(error)) void invalidate();
        },
    });
}

export interface ChangeSegmentStatusInput {
    segmentId: string;
    status: SegmentStatusWire;
    reason?: string;
}

/**
 * Archivar o reactivar → PUT /segments/{segmentId}/status. NO hay DELETE: borrar dejaría políticas
 * y promociones huérfanas, y archivado el nombre sigue resolviendo.
 */
export function useChangeSegmentStatus() {
    const invalidate = useInvalidateSegments();
    return useMutation<SegmentResponse, Error, ChangeSegmentStatusInput>({
        mutationFn: ({ segmentId, status, reason }) =>
            changeSegmentStatus(segmentId, status, reason),
        onSuccess: invalidate,
        onError: (error) => {
            if (isStaleWriteError(error)) void invalidate();
        },
    });
}

/** ¿En qué segmentos cae este cliente? → POST /segments/test. Es una lectura: no invalida nada. */
export function useTestCustomerSegments() {
    return useMutation<TestCustomerSegmentsResponse, Error, string>({
        mutationFn: (retailCustomerId) => testCustomerSegments({ retailCustomerId }),
    });
}

/** La bitácora de UN segmento, por páginas. Solo se lee con el diálogo abierto. */
export function useSegmentLog(segmentId: string | null, enabled: boolean): DefinitionLogState {
    const active = enabled && segmentId !== null;
    const query = useInfiniteQuery({
        queryKey: [SEGMENTS_KEY, 'log', segmentId ?? ''],
        queryFn: ({ pageParam }) =>
            listSegmentLog(segmentId ?? '', pageParam === null ? {} : { cursor: pageParam }),
        initialPageParam: null as string | null,
        getNextPageParam: (page) => page.nextCursor,
        enabled: active,
        staleTime: 30_000,
    });

    const entries = useMemo(
        () =>
            query.data
                ? query.data.pages.flatMap((page) => page.entries.map(toSegmentLogEntry))
                : [],
        [query.data],
    );

    const { fetchNextPage, refetch } = query;
    return {
        entries,
        isLoading: active && query.isLoading,
        error: active ? query.error : null,
        hasMore: query.hasNextPage,
        loadingMore: query.isFetchingNextPage,
        loadMore: () => {
            void fetchNextPage();
        },
        retry: () => {
            void refetch();
        },
    };
}
